import { SignalScaleCalculator } from "../core/signal-scale-calculator";
import { Plot } from "../../../../web-visualizator";
import { ObjectCreator, getItemByField } from "../../../../web-graphics";

export class OfflinePlotProvider {
  constructor(id, type, webglPlot) {
    this.id = id;
    this.type = type;
    this.webglPlot = webglPlot;
    this.hasAddedPoints = false;

    SignalScaleCalculator.generateScales(this.webglPlot);
    let signalsSettings = Plot.findContentSettings(this.webglPlot.settings, "signals", "central");
    this.viewSettings = {
      width: 12,
      scaleX: {
        current: null,
        values: [],
        unit: ""
      },
      scaleY: {
        current: null,
        values: [],
        unit: ""
      },
      filters: {
        current: [],
        allowed: []
      },
      linkedChannel: webglPlot.settings._linkedChannel
    };
    const channelSettings = this.webglPlot.getChannelSettings(
      this.viewSettings.linkedChannel.id_dataSource,
      this.viewSettings.linkedChannel.id
    );
    if (channelSettings.scaleX === undefined) {
      channelSettings.scaleX = {};
    }
    if (channelSettings.scaleY === undefined) {
      channelSettings.scaleY = {};
    }
    if (channelSettings.scaleX.values === undefined) {
      channelSettings.scaleX.values = [...signalsSettings.scaleX.values];
    }
    if (channelSettings.scaleY.values === undefined) {
      channelSettings.scaleY.values = [...signalsSettings.scaleY.values];
    }
    if (channelSettings.filters === undefined) {
      channelSettings.filters = [...signalsSettings.defaultFilters];
    }
    if (channelSettings.scaleX.index === undefined) {
      channelSettings.scaleX.index = signalsSettings.scaleX.defaultIndex ?? 0;
    }
    if (channelSettings.scaleY.index === undefined) {
      channelSettings.scaleY.index = signalsSettings.scaleY.defaultIndex ?? 0;
    }
    OfflinePlotProvider._setViewScales("scaleX", [...channelSettings.scaleX.values], this.viewSettings);
    OfflinePlotProvider._setViewScales("scaleY", [...channelSettings.scaleY.values], this.viewSettings);
    this.viewSettings.filters.allowed = ObjectCreator.deepCopy(signalsSettings.filters);
    this.viewSettings.filters.current = [...channelSettings.filters];
    this.viewSettings.scaleX.current = this.viewSettings.scaleX.values[channelSettings.scaleX.index];
    this.viewSettings.scaleY.current = this.viewSettings.scaleY.values[channelSettings.scaleY.index];

    let scaleXDistanceUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleX.distanceUnit)?.text ?? "";
    if (channelSettings.scaleX?.distanceUnit !== undefined) {
      scaleXDistanceUnit = this.webglPlot.getUnitSettings(channelSettings.scaleX.distanceUnit)?.text ?? "";
    }
    let scaleYDistanceUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleY.distanceUnit)?.text ?? "";
    if (channelSettings.scaleY?.distanceUnit !== undefined) {
      scaleYDistanceUnit = this.webglPlot.getUnitSettings(channelSettings.scaleY.distanceUnit)?.text ?? "";
    }
    let scaleXTimeUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleX.timeUnit)?.text ?? "";
    if (channelSettings.scaleX?.timeUnit !== undefined) {
      scaleXTimeUnit = this.webglPlot.getUnitSettings(channelSettings.scaleX.timeUnit)?.text ?? "";
    }
    let scaleYValueUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleY.valueUnit)?.text ?? "";
    if (channelSettings.scaleY?.valueUnit !== undefined) {
      scaleYValueUnit = this.webglPlot.getUnitSettings(channelSettings.scaleY.valueUnit)?.text ?? "";
    }

    this.viewSettings.scaleX.unit = `${scaleXDistanceUnit}/${scaleXTimeUnit}`;
    this.viewSettings.scaleY.unit = `${scaleYDistanceUnit}/${scaleYValueUnit}`;
  }

  init() {
    this.webglPlot.init();
  }

  addMark(sourceId, markId, data) {
    this.webglPlot.addMark(sourceId, markId, data);
  }

  addSignalPoints(sourceId, channelId, signalId, data) {
    this.hasAddedPoints = true;
    this.webglPlot.addPoints(sourceId, channelId, signalId, data);
  }

  addPoints(sourceId, channelId, messageSignals) {
    for (let signalIndex = 0; signalIndex < messageSignals.length; signalIndex++) {
      this.hasAddedPoints = true;
      this.webglPlot.addPoints(sourceId, channelId, signalIndex, messageSignals[signalIndex].data);
    }
  }

  afterAddPointsToChannels(channelDataInfo) {
    if (this.hasAddedPoints) {
      this.webglPlot.afterAddPointsToChannels(
        channelDataInfo.id_source,
        channelDataInfo.id_channel,
        channelDataInfo.blockSize,
        channelDataInfo.frameCounter
      );
      this.hasAddedPoints = false;
    }
  }

  setDefaultConfiguration(dataSourceId, channelId) {
    this.hasAddedPoints = false;
    this.webglPlot.setDefaultConfiguration(dataSourceId, channelId);
    this.webglPlot.onSignalLabelClicked(this.viewSettings.linkedChannel.id_dataSource, this.viewSettings.linkedChannel.id);
  }

  setConfiguration(dataSourceId, channelId, configName) {
    this.hasAddedPoints = false;
    this.webglPlot.setConfiguration(dataSourceId, channelId, configName);
    this.webglPlot.onSignalLabelClicked(this.viewSettings.linkedChannel.id_dataSource, this.viewSettings.linkedChannel.id);
  }

  setScaleX(newScaleX) {
    this.webglPlot.setScaleX(newScaleX);
  }

  setScaleY(newScaleY) {
    this.webglPlot.setScaleY(newScaleY);
    this.webglPlot.settings.canvas.refreshGraphics();
  }

  setFilters(newFiltersNames) {
    this.webglPlot.setFilters(newFiltersNames);
    this.webglPlot.settings.canvas.refreshGraphics();
  }

  setChannelScaleX(newScaleX, index) {
    //const leadRaySettings = getItemByField("name", "leadRay", this.webglPlot.settings.plotContent.aboveCentral);
    //const isSpeedSynchronized = leadRaySettings?.settings.synchronized ?? true;
    const isSpeedSynchronized = this.webglPlot.settings.synchronizedScaleX ?? true;
    if (isSpeedSynchronized) {
      const signalsSettings = getItemByField("name", "signals", this.webglPlot.settings.plotContent?.central);
      if (signalsSettings !== undefined) {
        signalsSettings.settings.scaleX.currIndex = index;
        signalsSettings.settings.scaleX.currValue = newScaleX;
      }
      this.webglPlot.setScaleX(newScaleX);
    } else {
      this.webglPlot.setChannelScaleX(this.viewSettings.linkedChannel.id_dataSource, this.viewSettings.linkedChannel.id, newScaleX);
    }
  }

  setChannelScaleY(newScaleY) {
    this.webglPlot.setChannelScaleY(this.viewSettings.linkedChannel.id_dataSource, this.viewSettings.linkedChannel.id, newScaleY);
    this.webglPlot.settings.canvas.refreshGraphics();
  }

  setChannelFilters(newFiltersNames) {
    this.webglPlot.setChannelFilters(this.viewSettings.linkedChannel.id_dataSource, this.viewSettings.linkedChannel.id, newFiltersNames);
    this.webglPlot.settings.canvas.refreshGraphics();
  }

  setChannelUnit(sourceId, channelName, unit) {
    this.webglPlot.setChannelUnit(sourceId, channelName, unit);
    this.webglPlot.settings.canvas.refreshGraphics();
  }

  setMeanderUnit(unit) {
    this.webglPlot.setMeanderUnit(unit);
    let channelSettings = this.webglPlot.getChannelSettings(
      this.viewSettings.linkedChannel.id_dataSource,
      this.viewSettings.linkedChannel.id
    );
    let signalsSettings = Plot.findContentSettings(this.webglPlot.settings, "signals", "central");
    let scaleYDistanceUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleY.distanceUnit)?.text ?? "";
    if (channelSettings.scaleY?.distanceUnit !== undefined) {
      scaleYDistanceUnit = this.webglPlot.getUnitSettings(channelSettings.scaleY.distanceUnit)?.text ?? "";
    }
    this.viewSettings.scaleY.unit = `${scaleYDistanceUnit}/${unit}`;
    this.webglPlot.settings.canvas.refreshGraphics();
    this.onViewSettingsChanged?.(this.id);
  }

  getSelectedChannelSettings() {
    return this.webglPlot.getChannelSettings(this.viewSettings.linkedChannel.id_dataSource, this.viewSettings.linkedChannel.id);
  }

  selectChannel(dataSourceId, channelId) {
    if (this.viewSettings.linkedChannel.id_dataSource == dataSourceId && this.viewSettings.linkedChannel.id == channelId) {
      return;
    }
    this.viewSettings.linkedChannel = {
      id_dataSource: dataSourceId,
      id: channelId
    };
    this.webglPlot.initChannelScaleXSettings(dataSourceId, channelId);
    this.webglPlot.initChannelScaleYSettings(dataSourceId, channelId);
    this.webglPlot.initChannelFilterSettings(dataSourceId, channelId);
    let channelSettings = this.webglPlot.getChannelSettings(dataSourceId, channelId);

    OfflinePlotProvider._setViewScales("scaleX", [...channelSettings.scaleX.values], this.viewSettings);
    OfflinePlotProvider._setViewScales("scaleY", [...channelSettings.scaleY.values], this.viewSettings);
    this.viewSettings.scaleX.current = this.viewSettings.scaleX.values[channelSettings.scaleX.index];
    this.viewSettings.scaleY.current = this.viewSettings.scaleY.values[channelSettings.scaleY.index];
    this.viewSettings.filters.current = [...channelSettings.filters];

    const unitText = this.webglPlot.getChannelUnitText(channelSettings);
    this.webglPlot.setMeanderUnit(unitText);
    this.webglPlot.setMeanderHeight(this.viewSettings.scaleY.current.value);

    let signalsSettings = Plot.findContentSettings(this.webglPlot.settings, "signals", "central");
    let scaleYDistanceUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleY.distanceUnit)?.text ?? "";
    if (channelSettings.scaleY?.distanceUnit !== undefined) {
      scaleYDistanceUnit = this.webglPlot.getUnitSettings(channelSettings.scaleY.distanceUnit)?.text ?? "";
    }
    let scaleYValueUnit = this.webglPlot.getUnitSettings(signalsSettings.scaleY.valueUnit)?.text ?? "";
    if (channelSettings.scaleY?.valueUnit !== undefined) {
      scaleYValueUnit = this.webglPlot.getUnitSettings(channelSettings.scaleY.valueUnit)?.text ?? "";
    }

    this.viewSettings.scaleY.unit = `${scaleYDistanceUnit}/${scaleYValueUnit}`;
    this.webglPlot.settings.canvas.refreshGraphics();

    this.onViewSettingsChanged?.(this.id);
  }

  static _setViewScales(scaleName, scales, viewSettings) {
    viewSettings[scaleName].values = [];
    for (let i = 0; i < scales.length; i++) {
      viewSettings[scaleName].values.push({
        value: scales[i],
        index: i
      });
    }
  }
}
